import { Phone, Mail, MapPin, Instagram, Facebook, Send, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import defaultLogo from "@/assets/logo-wtpc.png";

const Footer = () => {
  const { getSetting } = useSiteSettings();

  const logoUrl = getSetting("footer_logo_url") || defaultLogo;
  const siteTitle = getSetting("site_title", "World Transfers Punta Cana");
  const description = getSetting("site_description", "Tour & Transfers in Dominican Republic. Explore paradise with comfort and style.");
  const phone = getSetting("phone", "+1 (809) 123-4567");
  const email = getSetting("email", "info@worldtransferspuntacana.com");
  const address = getSetting("address", "Dominican Republic");
  const facebookUrl = getSetting("facebook_url");
  const instagramUrl = getSetting("instagram_url");

  const socialLinks = [
    { url: facebookUrl, icon: Facebook, label: "Facebook" },
    { url: instagramUrl, icon: Instagram, label: "Instagram" },
  ].filter((s) => s.url);

  return (
    <footer className="bg-[hsl(210,60%,10%)] text-white">
      {/* Newsletter */}
      <div className="border-b border-white/10">
        <div className="container mx-auto px-4 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display font-bold text-xl text-white mb-1">Stay Updated</h3>
              <p className="text-white/60 text-sm">Get exclusive deals and travel tips</p>
            </div>
            <div className="flex gap-2 w-full md:w-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 w-full md:w-64 rounded-none"
              />
              <Button className="bg-secondary text-secondary-foreground hover:bg-secondary/90 px-5 rounded-none">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="container mx-auto px-4 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <img src={logoUrl} alt={siteTitle} width={48} height={48} className="h-14 w-auto mb-5" />
            <p className="text-white/60 text-sm leading-relaxed mb-5">{description}</p>
            {socialLinks.length > 0 && (
              <div className="flex gap-2">
                {socialLinks.map((s) => (
                  <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 rounded-none bg-white/10 flex items-center justify-center text-white/60 hover:bg-secondary hover:text-secondary-foreground transition-all">
                    <s.icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display font-bold text-white mb-5 text-sm uppercase tracking-wider">Quick Links</h4>
            <div className="flex flex-col gap-2.5">
              {[
                { label: "Home", to: "/" },
                { label: "Tours", to: "/tours" },
                { label: "Transfers", to: "/transfers" },
                { label: "Fleet", to: "/fleet" },
                { label: "Destinations", to: "/destinations" },
                { label: "About", to: "/about" },
                { label: "Contact", to: "/contact" },
              ].map((link) => (
                <Link key={link.label} to={link.to}
                  className="text-white/60 hover:text-secondary text-sm transition-colors flex items-center gap-2 group">
                  <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Destinations */}
          <div>
            <h4 className="font-display font-bold text-white mb-5 text-sm uppercase tracking-wider">Popular Destinations</h4>
            <div className="flex flex-col gap-2.5">
              {["Punta Cana", "Santo Domingo", "Samaná", "Puerto Plata", "La Romana", "Bayahíbe"].map((place) => (
                <span key={place} className="text-white/60 text-sm flex items-center gap-2">
                  <MapPin className="w-3 h-3 text-secondary" />
                  {place}
                </span>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display font-bold text-white mb-5 text-sm uppercase tracking-wider">Contact Us</h4>
            <div className="flex flex-col gap-3">
              <a href={`tel:${phone.replace(/\s/g, "")}`} className="flex items-center gap-3 text-white/60 hover:text-secondary text-sm transition-colors">
                <div className="w-9 h-9 rounded-none bg-white/10 flex items-center justify-center">
                  <Phone className="w-4 h-4" />
                </div>
                {phone}
              </a>
              <a href={`mailto:${email}`} className="flex items-center gap-3 text-white/60 hover:text-secondary text-sm transition-colors">
                <div className="w-9 h-9 rounded-none bg-white/10 flex items-center justify-center">
                  <Mail className="w-4 h-4" />
                </div>
                {email}
              </a>
              <span className="flex items-center gap-3 text-white/60 text-sm">
                <div className="w-9 h-9 rounded-none bg-white/10 flex items-center justify-center">
                  <MapPin className="w-4 h-4" />
                </div>
                {address}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 py-5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} {siteTitle}. All rights reserved.
          </p>
          <div className="flex gap-6">
            {[
              { label: "Privacy Policy", to: "/privacy-policy" },
              { label: "Terms & Conditions", to: "/terms" },
              { label: "Cookie Policy", to: "/cookie-policy" },
            ].map((link) => (
              <Link key={link.to} to={link.to} className="text-white/40 hover:text-secondary text-sm transition-colors">
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
